import { FC, useCallback, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { isNil } from 'lodash/fp';
import { useFormContext } from 'react-hook-form';

import Icon from 'components/Icons/Icon';

import { usePatientModel } from 'components/Patient/usePatientModel';

import { useConsentVerification } from 'components/Ticket/TicketForms/useConsentVerification';
import { FormRadioGroup } from 'components/UIkit/atoms/RadioButton';
import { Text } from 'components/UIkit/atoms/Text';

export const MessagingConsentFormRadioGroup: FC = () => {
  const patient = usePatientModel();
  const { formState } = useFormContext();

  const {
    recipientName,
    isNewContactInfoIncomplete,
    getVerificationDate,
    isConsentVerificationEnabled
  } = useConsentVerification();

  const color = formState.errors.messagingConsent ? 'error' : 'primary';

  const renderYesOptionContent = useCallback(() => {
    const baseContent = (
      <Text variant="body2" color={color}>
        <Text variant="body1" color={color}>
          Yes:
        </Text>{' '}
        Patient consents to updates via text message, and understands this may contain personal
        health information.
      </Text>
    );

    if (!isConsentVerificationEnabled) {
      return baseContent;
    }

    let verificationContent = null;

    // Handle verified consent case - only show if we have requestConsentMessageStatus
    if (patient?.hasMessagingConsent && patient?.requestConsentMessageStatus) {
      verificationContent = (
        <Box display="flex" alignItems="center" gap={4} mt={10}>
          <Icon.SuccessCheckmark />
          <Typography variant="body1" color="primary.dark">
            Consent Verified via SMS on {getVerificationDate()}
          </Typography>
        </Box>
      );
    }

    // Handle new consent case
    const hasNoConsentData =
      isNil(patient?.messagingConsent) && isNil(patient?.isMessagingConsented);
    if (hasNoConsentData) {
      verificationContent = (
        <Box key="verification-pending">
          <Text variant="body2" fontStyle="italic" color="secondary">
            {recipientName} will receive a text message to verify consent.
          </Text>
        </Box>
      );
    }

    return (
      <>
        {baseContent}
        {verificationContent}
      </>
    );
  }, [
    color,
    isConsentVerificationEnabled,
    patient?.hasMessagingConsent,
    patient?.messagingConsent,
    patient?.isMessagingConsented,
    patient?.requestConsentMessageStatus,
    recipientName,
    getVerificationDate
  ]);

  const renderNoOptionContent = useCallback(() => {
    const baseContent = (
      <Text variant="body2" color={color}>
        <Text variant="body1" color={color}>
          No:
        </Text>{' '}
        Opt out of text messages.
      </Text>
    );

    // Only show decline status if we have requestConsentMessageStatus
    if (
      !isConsentVerificationEnabled ||
      !patient?.requestConsentMessageStatus ||
      patient?.isMessagingConsented !== false
    ) {
      return baseContent;
    }

    return (
      <>
        {baseContent}
        <Box display="flex" alignItems="center" gap={4}>
          <Text variant="body2" color="secondary" fontStyle="italic">
            Consent declined via SMS on {getVerificationDate()}
          </Text>
        </Box>
      </>
    );
  }, [
    color,
    getVerificationDate,
    isConsentVerificationEnabled,
    patient?.isMessagingConsented,
    patient?.requestConsentMessageStatus
  ]);

  const consentOptions = useMemo(
    () => [
      {
        label: renderYesOptionContent(),
        value: 'true',
        id: 'messagingConsentYes',
        disabled: isConsentVerificationEnabled && isNewContactInfoIncomplete
      },
      {
        label: renderNoOptionContent(),
        value: 'false',
        id: 'messagingConsentNo'
      }
    ],
    [
      renderYesOptionContent,
      renderNoOptionContent,
      isConsentVerificationEnabled,
      isNewContactInfoIncomplete
    ]
  );

  return (
    <>
      <Text variant="h4" component="h4" color={color} pb={20}>
        Can we text you?
      </Text>
      <FormRadioGroup name="messagingConsent" options={consentOptions} required />
    </>
  );
};
